import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { api, normalizeLangCode } from "../../../mock/api";

const LANDING_LANG_STORAGE_KEY = "landing_language_code";
const LANDING_LANG_QUERY_KEY = "lang";

const hasWindow = () => typeof window !== "undefined";

const readStoredCode = () => {
  if (!hasWindow()) return "";
  return normalizeLangCode(window.localStorage.getItem(LANDING_LANG_STORAGE_KEY) || "");
};

const readQueryCode = () => {
  if (!hasWindow()) return "";
  const params = new URLSearchParams(window.location.search);
  return normalizeLangCode(params.get(LANDING_LANG_QUERY_KEY) || "");
};

const writeStoredCode = (code: string) => {
  if (!hasWindow()) return;
  window.localStorage.setItem(LANDING_LANG_STORAGE_KEY, code);
};

const writeQueryCode = (code: string, replace = false) => {
  if (!hasWindow()) return;

  const url = new URL(window.location.href);
  const current = normalizeLangCode(url.searchParams.get(LANDING_LANG_QUERY_KEY) || "");
  if (current === code) return;

  url.searchParams.set(LANDING_LANG_QUERY_KEY, code);
  const next = `${url.pathname}${url.search}${url.hash}`;

  if (replace) {
    window.history.replaceState({}, "", next);
    return;
  }

  window.history.pushState({}, "", next);
};

export const useLandingLanguages = () => {
  const { i18n } = useTranslation();
  const [availableCodes, setAvailableCodes] = useState<string[]>(["ua"]);

  const currentCode = useMemo(() => normalizeLangCode(i18n.language), [i18n.language]);

  useEffect(() => {
    let mounted = true;

    api.getLandingCodes().then((codes) => {
      if (!mounted) return;
      setAvailableCodes(codes.length ? codes : ["ua"]);
    });

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (!availableCodes.length) return;

    const fallbackCode = availableCodes.includes("ua") ? "ua" : availableCodes[0];
    const query = readQueryCode();
    const stored = readStoredCode();
    const target =
      query && availableCodes.includes(query)
        ? query
        : stored && availableCodes.includes(stored)
        ? stored
        : fallbackCode;

    if (stored !== target) {
      writeStoredCode(target);
    }

    writeQueryCode(target, true);

    if (currentCode !== target) {
      i18n.changeLanguage(target);
    }
  }, [availableCodes, currentCode, i18n]);

  useEffect(() => {
    if (!hasWindow()) return;

    const onPopState = () => {
      const query = readQueryCode();
      if (!query || !availableCodes.includes(query)) return;

      writeStoredCode(query);
      if (currentCode !== query) {
        i18n.changeLanguage(query);
      }
    };

    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, [availableCodes, currentCode, i18n]);

  const setLanguage = (code: string) => {
    const normalized = normalizeLangCode(code);
    if (!availableCodes.includes(normalized)) return;

    writeStoredCode(normalized);
    writeQueryCode(normalized);

    if (currentCode !== normalized) {
      i18n.changeLanguage(normalized);
    }
  };

  return {
    availableCodes,
    currentCode,
    setLanguage,
  };
};
